import { useMemo } from "react";
import {
  contentIsNewerThanSubmission,
  readWhatsAppEditorContent,
  type WhatsAppTemplateCategory,
} from "@hightouch/lib/cep/types/whatsapp-template";
import type { WhatsAppTemplateContent } from "@hightouch/lib/cep/types/whatsapp-template-content";
import { validateWhatsAppContent } from "@hightouch/lib/cep/whatsapp/content-validation";

import {
  useGetCepWhatsAppMessageTemplateQuery,
  type GetCepWhatsAppMessageTemplateQuery,
} from "src/graphql";
import { useWhatsAppTemplateRowPoll } from "src/cep/assets/whatsapp-templates/use-whatsapp-template-row-poll";
import { asSentence } from "src/cep/messages/components/message-builder/whatsapp/whatsapp-status-timestamps";
import type { MessageContent } from "src/cep/messages/message/message-content-utils";
import { ChannelType } from "src/types/cep";

type TemplateRow = NonNullable<
  GetCepWhatsAppMessageTemplateQuery["message_contents"][number]["cep_whatsapp_template"]
>;

export type WhatsAppMessageTemplate = {
  template: TemplateRow;
  locale: TemplateRow["cep_whatsapp_template_locales"][number] | undefined;
  content: WhatsAppTemplateContent;
};

/** Excludes a live campaign's draft copy, which only its draft's content rows point at. */
const PUBLISHED_TEMPLATE = { forked_from_template_id: { _is_null: true } };

/**
 * The template a WhatsApp message sends. Its content, category and approval
 * all live on the template; the message's content rows only point at it.
 * Null until the template exists, which campaign creation normally ensures.
 */
export function useWhatsAppMessageTemplate(
  messageId: string,
  {
    enabled = true,
    poll = false,
    lane,
  }: {
    enabled?: boolean;
    /** Refetch on the template row poll, for a surface showing Meta's status. */
    poll?: boolean;
    /**
     * "published" reads the template the campaign sends. "staged" reads the one
     * an edit changes: the draft's copy while a live campaign has a draft.
     */
    lane: "published" | "staged";
  },
): {
  data: WhatsAppMessageTemplate | null;
  savedSenderIdentityId: string | null | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { refetchInterval } =
    useWhatsAppTemplateRowPoll<GetCepWhatsAppMessageTemplateQuery>();
  const query = useGetCepWhatsAppMessageTemplateQuery(
    lane === "published"
      ? { messageId, template: PUBLISHED_TEMPLATE }
      : { messageId },
    { enabled, refetchInterval: poll ? refetchInterval : false },
  );
  const { data, isLoading } = query;
  const isError = query.isError && data === undefined;
  const template = data?.message_contents[0]?.cep_whatsapp_template;
  const messageTemplate = useMemo(() => {
    if (!template) return null;
    const locale = template.cep_whatsapp_template_locales[0];
    return {
      template,
      locale,
      content: readWhatsAppEditorContent(locale?.editor_state),
    };
  }, [template]);
  const content = data?.message_contents[0];
  const savedSenderIdentityId =
    content === undefined
      ? undefined
      : (content.message?.cep_sender_identity_id ?? null);
  return { data: messageTemplate, savedSenderIdentityId, isLoading, isError };
}

/**
 * Whether the template still has to go to Meta: never sent, or edited since.
 * Approval freezes the content, so an approved template never needs it.
 */
export function whatsAppTemplateNeedsSubmission(
  locale: WhatsAppMessageTemplate["locale"],
): boolean {
  if (!locale || locale.approved_at) return false;
  return (
    !locale.submitted_at ||
    contentIsNewerThanSubmission({
      contentUpdatedAt: locale.content_updated_at,
      submittedAt: locale.submitted_at,
    })
  );
}

/**
 * Why the template can't be submitted to Meta, or null when it can
 */
export function whatsAppTemplateSubmitBlock(
  locale: WhatsAppMessageTemplate["locale"],
): { reason?: string } | null {
  if (!locale) return {};
  if (locale.approved_at) {
    return {
      reason: "This template is approved. Make a copy to change its content.",
    };
  }
  if (!whatsAppTemplateNeedsSubmission(locale)) {
    return { reason: "Already sent to Meta." };
  }
  const content = readWhatsAppEditorContent(locale.editor_state);
  if (content.body.trim() === "") {
    return { reason: "Add a body to submit this template." };
  }
  // Named here, since the editor doesn't outline an error on an empty field.
  const contentError = firstWhatsAppContentError(content);
  if (contentError !== null) {
    return {
      reason: `Fix the content to submit this template: ${asSentence(contentError)}`,
    };
  }
  return null;
}

/** Why Meta would refuse the content as written, or null. Examples don't count: the submit dialog collects them. */
export function firstWhatsAppContentError(
  content: WhatsAppTemplateContent,
): string | null {
  return (
    validateWhatsAppContent(content).fieldErrors.find(
      (entry) => entry.field.kind !== "sample",
    )?.message ?? null
  );
}

/** Whether any component has been written, so there is something to preview or edit. */
export function hasWhatsAppContent(
  content: WhatsAppTemplateContent | null | undefined,
): boolean {
  if (!content) return false;
  return (
    content.body.trim() !== "" ||
    content.header !== null ||
    (content.footer ?? "").trim() !== "" ||
    content.buttons.length > 0
  );
}

/**
 * What a WhatsApp message's preview shows, since its content rows render
 * nothing. Undefined for any other channel, whose preview reads its row.
 */
export function useWhatsAppPreviewContent({
  messageId,
  channelType,
  lane,
}: {
  messageId: string;
  channelType: string | null;
  lane: "published" | "staged";
}): MessageContent | undefined {
  const isWhatsApp = channelType === ChannelType.Whatsapp;
  const { data } = useWhatsAppMessageTemplate(messageId, {
    enabled: isWhatsApp,
    lane,
  });
  return isWhatsApp ? { whatsapp: data?.content ?? null } : undefined;
}

/** Meta's approved category wins over the requested one, matching the send path. */
export function isWhatsAppMarketingTemplate(
  locale: WhatsAppMessageTemplate["locale"],
): boolean {
  const category = locale?.category ?? locale?.requested_category;
  return (
    category?.toUpperCase() === ("MARKETING" satisfies WhatsAppTemplateCategory)
  );
}
